import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

function ShipmentsPage() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);

  useEffect(() => {
    fetchShipments();
  }, []);

  const fetchShipments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/shipping/shipments');
      const data = response.data;
      if (data.success) {
        setShipments(data.data || []);
      } else {
        setShipments([]);
        setError(data.error || data.message || 'Błąd pobierania przesyłek');
      }
    } catch (err) {
      setError(err.message || 'Błąd pobierania przesyłek');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLabel = async (shipmentId) => {
    try {
      const response = await api.get(`/shipping/shipments/${shipmentId}/label`, {
        responseType: 'blob'
      });
      const blob = response.data;
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `etykieta_${shipmentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Błąd pobierania etykiety');
    }
  };

  const handleRefreshStatus = async (shipmentId) => {
    try {
      await api.get(`/shipping/shipments/${shipmentId}/status`);
      fetchShipments(); // Odśwież listę
    } catch (err) {
      toast.error('Błąd odświeżania statusu');
    }
  };

  const handleCancelShipment = async (shipmentId) => {
    if (!window.confirm('Czy na pewno chcesz anulować tę przesyłkę?')) return;
    
    try {
      await api.post(`/shipping/shipments/${shipmentId}/cancel`);
      fetchShipments(); // Odśwież listę
    } catch (err) {
      toast.error('Błąd anulowania przesyłki');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'created': 'var(--warning)',
      'confirmed': 'var(--info)',
      'offers_prepared': 'var(--info)',
      'offer_selected': 'var(--info)',
      'dispatched_by_sender': 'var(--info)',
      'collected_from_sender': 'var(--info)',
      'taken_by_courier': 'var(--info)',
      'adopted_at_source_branch': 'var(--info)',
      'sent_from_source_branch': 'var(--info)',
      'ready_to_pickup': 'var(--success)',
      'out_for_delivery': 'var(--success)',
      'delivered': 'var(--success)',
      'pickup_reminder_sent': 'var(--warning)',
      'avizo': 'var(--warning)',
      'customer_delivering': 'var(--warning)',
      'returned_to_sender': 'var(--danger)',
      'cancelled': 'var(--danger)'
    };
    return colors[status] || 'var(--text-muted)';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'created': 'Utworzona',
      'confirmed': 'Potwierdzona',
      'offers_prepared': 'Oferty gotowe',
      'offer_selected': 'Oferta wybrana',
      'dispatched_by_sender': 'Nadana',
      'collected_from_sender': 'Odebrana od nadawcy',
      'taken_by_courier': 'U kuriera',
      'adopted_at_source_branch': 'W oddziale',
      'sent_from_source_branch': 'W transporcie',
      'ready_to_pickup': 'Gotowa do odbioru',
      'out_for_delivery': 'W doręczeniu',
      'delivered': 'Doręczona',
      'pickup_reminder_sent': 'Przypomnienie',
      'avizo': 'Awizo',
      'customer_delivering': 'Klient odbiera',
      'returned_to_sender': 'Zwrot',
      'cancelled': 'Anulowana'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        ⏳ Ładowanie przesyłek...
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>📦</span>
            Przesyłki InPost
          </h2>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>
            Zarządzanie przesyłkami kurierskimi
          </p>
        </div>
        <button
          onClick={fetchShipments}
          style={{
            padding: '0.5rem 1rem',
            border: '1px solid var(--info)', color: 'var(--info)' ,
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          🔄 Odśwież
        </button>
      </div>

      {error && (
        <div style={{
          padding: '0.75rem',
          background: 'var(--danger-light)',
          color: 'var(--danger)',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Tabela przesyłek */}
      <div style={{
        background: 'var(--card-bg)',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '12px', fontWeight: '600' }}>
                Nr śledzenia
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '12px', fontWeight: '600' }}>
                Zamówienie
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '12px', fontWeight: '600' }}>
                Odbiorca
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '12px', fontWeight: '600' }}>
                Usługa
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
                Status
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '12px', fontWeight: '600' }}>
                Data
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
                Akcje
              </th>
            </tr>
          </thead>
          <tbody>
            {shipments.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  📭 Brak przesyłek
                </td>
              </tr>
            ) : (
              shipments.map(shipment => (
                <tr key={shipment.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.75rem', fontSize: '12px' }}>
                    <strong style={{ fontFamily: 'monospace' }}>
                      {shipment.tracking_number || '—'}
                    </strong>
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '12px' }}>
                    {shipment.numer_zamowienia || `#${shipment.order_id || '—'}`}
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '12px' }}>
                    <div>{shipment.receiver_name}</div>
                    {shipment.target_point && (
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                        📦 {shipment.target_point}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '11px' }}>
                    {shipment.service === 'inpost_locker_standard' && '📦 Paczkomat'}
                    {shipment.service === 'inpost_courier_standard' && '🚚 Kurier'}
                    {shipment.service?.includes('express') && '⚡ Express'}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '600',
                      color: '#fff' ,
                      background: getStatusColor(shipment.status)
                    }}>
                      {getStatusLabel(shipment.status)}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '11px', color: 'var(--text-muted)' }}>
                    {new Date(shipment.created_at).toLocaleDateString('pl-PL', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleRefreshStatus(shipment.inpost_id)}
                        title="Odśwież status"
                        style={{
                          padding: '0.25rem 0.5rem',
                          border: '1px solid var(--info)', color: 'var(--info)' ,
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        🔄
                      </button>
                      <button
                        onClick={() => handleDownloadLabel(shipment.inpost_id)}
                        title="Pobierz etykietę"
                        style={{
                          padding: '0.25rem 0.5rem',
                          border: '1px solid var(--success)', color: 'var(--success)' ,
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        🏷️
                      </button>
                      {shipment.status === 'created' && (
                        <button
                          onClick={() => handleCancelShipment(shipment.inpost_id)}
                          title="Anuluj"
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid var(--danger)', color: 'var(--danger)' ,
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '11px'
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Info */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: 'var(--info-light)',
        borderRadius: '8px',
        border: '1px solid var(--info-light)',
        fontSize: '12px'
      }}>
        <h4 style={{ margin: '0 0 0.5rem', color: 'var(--info)' }}>
          ℹ️ Informacje
        </h4>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--text-secondary)' }}>
          <li>Przesyłki są tworzone automatycznie przy wysyłce zamówienia</li>
          <li>Kliknij 🏷️ aby pobrać etykietę do wydruku</li>
          <li>Status jest aktualizowany automatycznie przez API InPost</li>
          <li>Śledzenie przesyłki: <a href="https://inpost.pl/sledzenie-przesylek" target="_blank" rel="noopener noreferrer">inpost.pl/sledzenie</a></li>
        </ul>
      </div>
    </div>
  );
}

export default ShipmentsPage;
